import { useState, type ChangeEvent, type Dispatch } from 'react';
import { ArrowDown2, Setting4 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { isOptionFieldType, resolveFieldType, supportsPlaceholder } from '../builderUtils';
import { OptionEditor } from './OptionEditor';
import { QuestionTypeSelect } from './QuestionTypeSelect';
import { RatingEditor } from './RatingEditor';
import type { BuilderAction } from '../builderReducer';
import type { SurveyQuestion } from '@/api/surveys';

interface QuestionEditorProps {
    dispatch: Dispatch<BuilderAction>;
    question: SurveyQuestion;
}

export function QuestionEditor({ dispatch, question }: QuestionEditorProps) {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const questionId = question.id;

    if (!questionId) return null;

    const fieldType = resolveFieldType(question);
    const shouldShowPlaceholder = supportsPlaceholder(fieldType);

    return (
        <div className="space-y-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
                <div className="space-y-2">
                    <Label>Enunciado</Label>
                    <Input
                        value={question.field_label ?? ''}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            dispatch({
                                type: 'updateQuestionLabel',
                                questionId,
                                value: event.target.value,
                            })
                        }
                        placeholder="Ex.: Qual é sua principal queixa?"
                        className="text-base font-medium"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Tipo</Label>
                    <QuestionTypeSelect
                        dispatch={dispatch}
                        fieldType={fieldType}
                        questionId={questionId}
                    />
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-2 text-sm text-foreground">
                    <input
                        type="checkbox"
                        checked={question.required}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            dispatch({
                                type: 'updateQuestion',
                                questionId,
                                patch: { required: event.target.checked },
                            })
                        }
                    />
                    Obrigatória
                </label>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAdvanced((current) => !current)}
                >
                    <Setting4 className="size-4" />
                    Avançado
                    <ArrowDown2
                        className={cn(
                            'size-4 transition-transform',
                            showAdvanced ? 'rotate-180' : '',
                        )}
                    />
                </Button>
            </div>

            {showAdvanced ? (
                <div className="grid gap-4 rounded-2xl border border-border/70 bg-background p-4 sm:grid-cols-2">
                    <div className="space-y-2">
                        <Label>Texto de ajuda</Label>
                        <Input
                            value={question.config?.help_text ?? ''}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                dispatch({
                                    type: 'updateQuestion',
                                    questionId,
                                    patch: {
                                        config: { help_text: event.target.value },
                                    },
                                })
                            }
                            placeholder="Opcional"
                        />
                    </div>
                    {shouldShowPlaceholder ? (
                        <div className="space-y-2">
                            <Label>Placeholder</Label>
                            <Input
                                value={question.config?.placeholder ?? ''}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    dispatch({
                                        type: 'updateQuestion',
                                        questionId,
                                        patch: {
                                            config: { placeholder: event.target.value },
                                        },
                                    })
                                }
                                placeholder="Opcional"
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}

            {isOptionFieldType(fieldType) ? (
                <OptionEditor dispatch={dispatch} question={question} questionId={questionId} />
            ) : null}

            {fieldType === 'rating' ? (
                <RatingEditor dispatch={dispatch} question={question} questionId={questionId} />
            ) : null}
        </div>
    );
}
